"use client";

import { useEffect, useMemo, useRef } from "react";
import type { IndexedCard } from "../lib/opIndexTypes";
import { getOpIndex, getSet } from "../lib/opIndex";
import { buildCardmarketVersionsUrl, buildLigaOnePieceUrl } from "../lib/cardLinks";
import { AttributeBadges } from "./AttributeBadge";

interface CardDetailPanelProps {
  idPrefix: string;
  isOpen: boolean;
  cards: IndexedCard[];
  currentIndex: number | null;
  onClose: () => void;
  onNavigate: (nextIndex: number) => void;
  resolveImageUrl: (card: IndexedCard) => string;
}

function formatValue(value: string | number | null | undefined): string {
  if (value == null) return "-";
  const text = String(value).trim();
  return text || "-";
}

export function CardDetailPanel({
  idPrefix,
  isOpen,
  cards,
  currentIndex,
  onClose,
  onNavigate,
  resolveImageUrl,
}: CardDetailPanelProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const card =
    currentIndex != null && currentIndex >= 0 && currentIndex < cards.length
      ? cards[currentIndex]
      : null;
  const canPrev = currentIndex != null && currentIndex > 0;
  const canNext =
    currentIndex != null &&
    currentIndex >= 0 &&
    currentIndex < cards.length - 1;

  useEffect(() => {
    if (!isOpen || !card) return;
    dialogRef.current?.focus();
  }, [isOpen, card]);

  useEffect(() => {
    if (!isOpen || !card) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowLeft" && canPrev && currentIndex != null) {
        event.preventDefault();
        onNavigate(currentIndex - 1);
        return;
      }
      if (event.key === "ArrowRight" && canNext && currentIndex != null) {
        event.preventDefault();
        onNavigate(currentIndex + 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, card, onClose, onNavigate, canPrev, canNext, currentIndex]);

  const imageUrl = useMemo(() => {
    if (!card) return "";
    return resolveImageUrl(card);
  }, [card, resolveImageUrl]);

  const printedInSets = useMemo(() => {
    if (!card) return [];
    const byNumber = getOpIndex().byNumber[card.number] ?? [];
    const setCodes = Array.from(
      new Set(byNumber.map((entry) => entry.setCode.toUpperCase())),
    ).sort((a, b) => a.localeCompare(b));
    return setCodes.map((code) => {
      const set = getSet(code);
      return set ? `${code} - ${set.name}` : code;
    });
  }, [card]);

  if (!isOpen || !card || currentIndex == null) return null;

  const baseId = (card.id || "").split("_")[0] || card.number;

  return (
    <div
      id={`${idPrefix}-overlay`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3"
      onClick={onClose}
    >
      <div
        id={`${idPrefix}-dialog`}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        ref={dialogRef}
        className="relative flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-950 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          id={`${idPrefix}-close`}
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 z-10 rounded-full border border-slate-700 bg-slate-950/80 px-3 py-1 text-xs text-slate-200 hover:border-slate-500"
        >
          Close
        </button>

        <div className="grid flex-1 grid-cols-1 gap-3 overflow-auto p-3 md:grid-cols-[minmax(240px,380px)_1fr]">
          <div className="relative overflow-hidden rounded-lg border border-slate-800 bg-slate-900">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              id={`${idPrefix}-image`}
              src={imageUrl}
              alt={card.number}
              className="h-full w-full object-contain"
            />
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-slate-800 bg-slate-900/70 p-3">
            <div className="pr-14 text-center">
              <p id={`${idPrefix}-meta`} className="text-xs text-slate-400">
                {baseId} | {card.id} | {formatValue(card.type)} |{" "}
                {formatValue(card.class)}
              </p>
              <h3
                id={`${idPrefix}-title`}
                className="mt-1 text-lg font-semibold text-slate-100"
              >
                {card.name}
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-2 text-xs">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">
                    {card.class.toLowerCase() === "leader" ? "Life" : "Cost"}
                  </div>
                  <div className="text-slate-200">
                    {formatValue(card.costRaw || card.cost)}
                  </div>
                </div>
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">Attribute</div>
                  <div className="text-slate-200">
                    {card.attributes?.length ? (
                      <AttributeBadges attributes={card.attributes} size="md" />
                    ) : "-"}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">Power</div>
                  <div className="text-slate-200">{formatValue(card.power)}</div>
                </div>
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">Counter</div>
                  <div className="text-slate-200">{formatValue(card.counter)}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">Color</div>
                  <div className="text-slate-200">
                    {card.colors?.length ? card.colors.join(", ") : "-"}
                  </div>
                </div>
                <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                  <div className="text-slate-400">Block Icon</div>
                  <div className="text-slate-200">{formatValue(card.blockIcon)}</div>
                </div>
              </div>

              <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                <div className="text-slate-400">Trigger</div>
                <div className="text-slate-200">{formatValue(card.trigger)}</div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                <div className="text-slate-400">Type</div>
                <div className="text-slate-200">
                  {card.features?.length ? card.features.join(", ") : "-"}
                </div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                <div className="text-slate-400">Effect</div>
                <div className="whitespace-pre-wrap text-slate-200">
                  {formatValue(card.text)}
                </div>
              </div>
              <div className="rounded border border-slate-800 bg-slate-950/70 p-2">
                <div className="text-slate-400">Printed In</div>
                <ul className="list-disc space-y-0.5 pl-4 text-slate-200">
                  {(printedInSets.length ? printedInSets : [card.setCode]).map((setLine) => (
                    <li key={setLine}>{setLine}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-1 flex flex-wrap items-center justify-end gap-2">
              <a
                id={`${idPrefix}-ligaonepiece`}
                href={buildLigaOnePieceUrl(card)}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-emerald-600 px-3 py-1 text-xs text-emerald-300 hover:bg-emerald-950/40"
              >
                Liga OP
              </a>
              <a
                id={`${idPrefix}-cardmarket`}
                href={buildCardmarketVersionsUrl(card)}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-blue-600 px-3 py-1 text-xs text-blue-300 hover:bg-blue-950/40"
              >
                CM Versions
              </a>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-700 bg-slate-900/80 px-4 py-2">
          <button
            id={`${idPrefix}-prev`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (canPrev) onNavigate(currentIndex - 1);
            }}
            disabled={!canPrev}
            aria-label="Previous card"
            className="rounded-full border border-slate-600 bg-slate-950/90 px-4 py-1.5 text-sm text-slate-200 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ← Prev
          </button>
          <span className="text-xs text-slate-400">
            {currentIndex + 1} / {cards.length}
          </span>
          <button
            id={`${idPrefix}-next`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (canNext) onNavigate(currentIndex + 1);
            }}
            disabled={!canNext}
            aria-label="Next card"
            className="rounded-full border border-slate-600 bg-slate-950/90 px-4 py-1.5 text-sm text-slate-200 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
